import type { IQuote, IQuoteLine, IQuoteTextLine } from '@/interfaces'

/**
 * Normalizes quote line numbers and their text line references before API submission
 *
 * This function ensures that:
 * 1. All quote lines have sequential lineNum values (0, 1, 2, etc.)
 * 2. All text lines have sequential lineNum values (0, 1, 2, etc.)
 * 3. All text lines' afterLineNumber references match their parent quote line's lineNum
 *
 * This is necessary because line resequencing in the UI can cause mismatches between
 * line numbers and their text line references, which causes API validation errors.
 *
 * @param quote - The quote object to normalize
 * @returns A new quote object with normalized line numbers (does not mutate original)
 */
export function normalizeQuoteLineNumbers(quote: IQuote): IQuote {
	// If no quote lines, return as-is
	if (!quote.quoteLines || quote.quoteLines.length === 0) {
		return quote
	}

	// Create a shallow copy of the quote to avoid mutation
	const normalizedQuote = { ...quote }

	// Create normalized quote lines array
	const normalizedQuoteLines: IQuoteLine[] = []

	// Filter out deleted lines and sort by current lineNum
	const activeLines = quote.quoteLines.filter((line) => !line.isDeleted).sort((a, b) => a.lineNum - b.lineNum)

	// Resequence all quote lines and update their text lines
	activeLines.forEach((line, index) => {
		const newLineNum = index // Start at 0, not 1

		// Create a copy of the line with updated lineNum
		const normalizedLine: IQuoteLine = {
			...line,
			lineNum: newLineNum
		}

		// Update all text lines' afterLineNumber to match the parent's new lineNum
		// Also resequence text line numbers starting at 0
		if (line.textLines && Array.isArray(line.textLines)) {
			const normalizedTextLines: IQuoteTextLine[] = line.textLines
				.filter((textLine) => !textLine.isDeleted)
				.map((textLine, textLineIndex) => ({
					...textLine,
					lineNum: textLineIndex, // Resequence text line numbers starting at 0
					afterLineNumber: newLineNum // Update reference to parent line
				}))

			normalizedLine.textLines = normalizedTextLines as IQuoteLine['textLines']
		}

		normalizedQuoteLines.push(normalizedLine)
	})

	// Include deleted lines at the end (they still need to be sent to API for soft delete)
	const deletedLines = quote.quoteLines.filter((line) => line.isDeleted)
	normalizedQuoteLines.push(...deletedLines)

	// Update the quote with normalized lines
	normalizedQuote.quoteLines = normalizedQuoteLines as IQuote['quoteLines']

	return normalizedQuote
}
